import type { WorkflowEnginePin } from "@/api";
import React from "react";
import { Breadcrumb, StatusPill } from "@/ds";
import { Icon } from "@/lib";
import { useT } from "../../i18n";
import styles from "./email-review.module.css";

export function workspacePageTitle(subject: string | null | undefined, company: string): string {
  return subject?.trim() || company;
}

export function WorkspacePageHeading({
  subject,
  company,
  requestNumber,
  requestReference,
  isTest,
  enginePin,
  workflowUnavailable,
  onBack,
  backToEmailQa,
  assignee,
  actions,
}: {
  subject?: string | null;
  company: string;
  requestNumber?: string | null;
  requestReference?: string | null;
  /** #677: this request was injected for testing, not received from a customer. */
  isTest?: boolean;
  enginePin?: WorkflowEnginePin;
  workflowUnavailable?: boolean;
  onBack: () => void;
  backToEmailQa?: boolean;
  assignee?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  const t = useT();
  const title = workspacePageTitle(subject, company);

  return (
    <header className={styles.pageHeading} aria-labelledby="workspace-request-title">
      <div className={styles.pageHeadingTitle}>
        {/* #2174: the back arrow stands on the breadcrumb's line, so the subject and the line
            under it start at the page's left edge, like the content below them. */}
        <div className={styles.pageHeadingContext}>
          <button className="iconbtn" aria-label={t(backToEmailQa ? "testMail.backToEmailQa" : "ws.backToInbox")} onClick={onBack}>
            <Icon name="arrowLeft" size={16} />
          </button>
          <span onClick={(event) => {
            if ((event.target as HTMLElement).closest("a")) {
              event.preventDefault();
              onBack();
            }
          }}>
            <Breadcrumb items={[
              { label: t(backToEmailQa ? "emailQa.title" : "shell.overview"), href: "#" },
              { label: [t("ws.review.customerRequest"), requestNumber].filter(Boolean).join(" ") },
            ]} />
          </span>
        </div>
        <h1 id="workspace-request-title">
          <span className={styles.pageHeadingSubject}>{title}</span>
          {isTest && (
            <span
              className={styles.pageHeadingTestBadge}
              title={t("testMail.rowBadgeTitle")}
              data-testid="workspace-test-badge"
            >
              <StatusPill tone="warning" variant="soft" size="sm" label={t("ws.review.testRequest")} />
            </span>
          )}
        </h1>
        {(enginePin || title !== company || requestReference) && (
          <div className={styles.pageHeadingMeta}>
            {enginePin && <span>{t("workflow.mode")}: {t(`workflow.${enginePin.engine}`)}</span>}
            {workflowUnavailable && <span>{t("workflow.notReady")}</span>}
            {title !== company && <span data-testid="workspace-customer-context">{company}</span>}
            {title !== company && requestReference && <span aria-hidden="true">·</span>}
            {requestReference && <span>{requestReference}</span>}
          </div>
        )}
      </div>
      <div className={styles.pageHeadingActions}>
        {assignee}
        {actions}
      </div>
    </header>
  );
}
